import { useState } from 'react';
import { Link } from 'react-router-dom';
import { requestPermission } from '@/notifications/desktop';
import { useRealtime } from '@/notifications/RealtimeProvider';

const DismissedKey = 'wa.notifications.prompt.dismissed';

/**
 * The one-line offer to switch desktop popups on.
 *
 * <b>This bar exists because the browser will not let the app ask without it.</b>
 * `Notification.requestPermission()` must be called from a user gesture: Chrome
 * ignores a call made on page load and, worse, treats a repeated one as abuse and
 * blocks the origin — so there is no version of this that quietly asks on first
 * render. Something has to be clicked, which means something has to be offered.
 *
 * And it has to be offered <i>here</i>, not only in Settings. An agent who never
 * opens Settings → Notifications would otherwise have every switch on, receive every
 * event, and see nothing — because the browser was never asked. That is the shape of
 * bug that gets reported as "notifications don't work" and is unfalsifiable from the
 * server's side, since as far as the API is concerned every message was delivered.
 *
 * Three rules keep it from being the thing people hate about this app:
 *
 * - <b>Shown only at `default`.</b> Granted needs nothing; denied cannot be undone
 *   from a page — only from the browser's own site settings — so a bar offering to
 *   fix it would be a button that does nothing.
 * - <b>Dismissible, and the dismissal sticks.</b> Per browser, in localStorage,
 *   because it is a statement about this browser's permission.
 * - <b>One line, in the flow, above the screen.</b> Not a modal over the inbox. It
 *   is an offer, and an offer that blocks the work is a demand.
 */
export function NotificationPrompt() {
  const { permission, refreshPermission, preferences } = useRealtime();

  const [dismissed, setDismissed] = useState(() => {
    // Private windows and blocked site data both throw on read. A prompt that
    // cannot remember being dismissed is a small annoyance; a shell that fails to
    // render is not.
    try { return localStorage.getItem(DismissedKey) === '1'; } catch { return false; }
  });

  const [asking, setAsking] = useState(false);

  // Nothing to offer: already answered, unsupported, the agent has said no thanks,
  // or they have popups switched off in their own preferences — in which case the
  // browser's permission is beside the point and asking for it would be asking for
  // something they have already declined.
  if (permission !== 'default' || dismissed || !preferences.desktop) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DismissedKey, '1'); } catch { /* nothing to do */ }
  };

  const enable = async () => {
    setAsking(true);
    await requestPermission();
    // Whatever the answer, the browser's state has moved off `default` unless the
    // prompt was dismissed without choosing — in which case this bar correctly
    // stays.
    refreshPermission();
    setAsking(false);
  };

  return (
    <div
      role="region"
      aria-label="Desktop notifications"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-(--color-line) bg-(--color-brand-wash) px-3 py-2 sm:px-4"
    >
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4.5 shrink-0 text-(--color-brand)">
        <path
          d="M10 3.2a4.4 4.4 0 0 0-4.4 4.4v2.6L4.2 13h11.6l-1.4-2.8V7.6A4.4 4.4 0 0 0 10 3.2Z"
          fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
        />
        <path d="M8.2 15.2a1.9 1.9 0 0 0 3.6 0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>

      <p className="min-w-0 flex-1 text-small text-(--color-ink)">
        Get told about new messages while this tab is in the background.{' '}
        <Link to="/settings/notifications" className="underline decoration-(--color-brand)/40 hover:decoration-(--color-brand)">
          Choose what you hear about
        </Link>
        .
      </p>

      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => void enable()}
          disabled={asking}
          className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-1.5 text-small font-semibold text-(--color-brand-ink) transition hover:opacity-90 disabled:opacity-60"
        >
          Turn on
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="tap rounded-(--radius-field) px-2.5 py-1.5 text-small text-(--color-muted) transition hover:bg-(--color-surface) hover:text-(--color-ink)"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
